import { useMemo } from 'react';
import { TIME_GUTTER_CLASS } from '../constants';
import { DAY_NAMES } from '../types';
import type { EventInstance, TemperatureUnit, TimeFormat } from '../types';
import type { WeatherDay } from '../types';
import {
  getWeekDays,
  getAllDayEvents,
  isToday,
  formatDateKey,
  addDays,
  getWeekNumber,
  formatTemperature,
} from '../utils';
import { AllDayRow } from './AllDayRow';
import { useScrollbarWidth } from '../hooks/useScrollbarWidth';
import { TimeGrid } from './TimeGrid';
import { WeatherIcon } from './WeatherIcon';

interface WeekViewProps {
  currentDate: Date;
  events: EventInstance[];
  weekStartDay: number;
  workingHoursStart: number;
  timeFormat?: TimeFormat;
  onSelectRange: (start: Date, end: Date) => void;
  onClickEvent: (event: EventInstance) => void;
  resizingEventId?: string | null;
  resizeHeight?: number | null;
  onResizeStart?: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
  draggingEventId?: string | null;
  dragOffset?: { x: number; y: number } | null;
  onEventDragStart?: (eventId: string, e: React.MouseEvent) => void;
  weather?: Map<string, WeatherDay>;
  showWeekends?: boolean;
  showWeekNumbers?: boolean;
  temperatureUnit?: TemperatureUnit;
  hourHeightPx?: number;
  weekViewAnchor?: 'fixed' | 'rolling';
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  showConflictWarnings?: boolean;
  minBreakBetweenEvents?: number;
  snapMinutes?: number;
  defaultDurationMinutes?: number;
}

export function WeekView({
  currentDate,
  events,
  weekStartDay,
  workingHoursStart,
  timeFormat,
  onSelectRange,
  onClickEvent,
  resizingEventId,
  resizeHeight,
  onResizeStart,
  draggingEventId,
  dragOffset,
  onEventDragStart,
  weather,
  showWeekends = true,
  showWeekNumbers = false,
  temperatureUnit,
  hourHeightPx,
  weekViewAnchor = 'fixed',
  dimPastEvents = false,
  eventColorPriority = 'event',
  showConflictWarnings = false,
  minBreakBetweenEvents = 0,
  snapMinutes,
  defaultDurationMinutes,
}: WeekViewProps) {
  const allDays = useMemo(() => {
    if (weekViewAnchor === 'rolling') {
      const days: Date[] = [];
      for (let i = 0; i < 7; i++) {
        days.push(addDays(currentDate, i));
      }
      return days;
    }
    return getWeekDays(currentDate, weekStartDay);
  }, [currentDate, weekStartDay, weekViewAnchor]);

  const days = useMemo(
    () => (showWeekends ? allDays : allDays.filter((d) => d.getDay() !== 0 && d.getDay() !== 6)),
    [allDays, showWeekends],
  );

  const scrollbarGutterPx = useScrollbarWidth();

  // All-day events per day
  const allDayColumns = days.map((day) => ({
    date: day,
    events: getAllDayEvents(events, day),
  }));

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Day headers — reserve the time grid's scrollbar gutter so the header
          columns and the grid columns below them stay in line. */}
      <div className="flex border-b" style={{ paddingRight: scrollbarGutterPx }}>
        <div
          {...(showWeekNumbers && days[0]
            ? { 'aria-label': `Week ${getWeekNumber(days[0])}` }
            : {})}
          className={`${TIME_GUTTER_CLASS} flex items-center justify-center text-[10px] tabular-nums text-muted-foreground`}
        >
          {showWeekNumbers && days[0] && `W${getWeekNumber(days[0])}`}
        </div>
        <div
          className="flex-1 grid min-w-0"
          style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}
        >
          {days.map((day) => {
            const today = isToday(day);
            const dateKey = formatDateKey(day);
            const dayWeather = weather?.get(dateKey);
            return (
              <div
                key={dateKey}
                className={`text-center py-1.5 border-r last:border-r-0 min-w-0 overflow-hidden ${
                  today ? 'bg-primary/5' : ''
                }`}
              >
                <div className="text-[10px] text-muted-foreground uppercase">
                  {DAY_NAMES[day.getDay()]}
                </div>
                <div
                  className={`text-sm font-medium ${
                    today
                      ? 'bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center mx-auto'
                      : ''
                  }`}
                >
                  {day.getDate()}
                </div>
                {dayWeather && (
                  <div className="flex items-center justify-center gap-0.5 mt-0.5">
                    <WeatherIcon code={dayWeather.weatherCode} size={14} />
                    <span className="text-[10px] text-muted-foreground">
                      {formatTemperature(dayWeather.tempMax, temperatureUnit)}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* All-day row */}
      <AllDayRow
        dayColumns={allDayColumns}
        onClickEvent={onClickEvent}
        eventColorPriority={eventColorPriority}
        scrollbarGutterPx={scrollbarGutterPx}
      />

      {/* Time grid */}
      <TimeGrid
        days={days}
        events={events}
        workingHoursStart={workingHoursStart}
        timeFormat={timeFormat}
        onSelectRange={onSelectRange}
        onClickEvent={onClickEvent}
        resizingEventId={resizingEventId}
        resizeHeight={resizeHeight}
        onResizeStart={onResizeStart}
        draggingEventId={draggingEventId}
        dragOffset={dragOffset}
        onEventDragStart={onEventDragStart}
        hourHeightPx={hourHeightPx}
        dimPastEvents={dimPastEvents}
        eventColorPriority={eventColorPriority}
        showConflictWarnings={showConflictWarnings}
        minBreakBetweenEvents={minBreakBetweenEvents}
        snapMinutes={snapMinutes}
        defaultDurationMinutes={defaultDurationMinutes}
      />
    </div>
  );
}
